import { changePassword as changePasswordApi, sendResetLinkEmail } from '../api';
import { Env } from '@/shared/config/environment';

const RESET_LINK_TIMEOUT = 60;

const state = {
  email: null,
  resetLinkRequestTime: null,
  isLoadingResetLink: false,
  isResetLinkError: false,
  resetLinkError: null,
  isLoadingChangePassword: false,
  changePasswordError: null,
};

const mutations = {
  SET_RESET_EMAIL: (state, email) => {
    state.email = email;
    state.resetLinkRequestTime = Date.now();
  },
  SET_LOADING_RESET_LINK: (state, isLoading) => {
    state.isLoadingResetLink = isLoading;
  },
  SET_RESET_LINK_ERROR: (state, error) => {
    state.isResetLinkError = !!error;
    state.resetLinkError = error;
  },
  SET_LOADING_CHANGE_PASSWORD: (state, isLoading) => {
    state.isLoadingChangePassword = isLoading;
  },
  SET_CHANGE_PASSWORD_ERROR: (state, error) => {
    state.changePasswordError = error;
  },
};

const actions = {
  async sendResetLink({ commit, dispatch, state }, email) {
    const secondsElapsed = state.resetLinkRequestTime
      ? Math.floor((Date.now() - state.resetLinkRequestTime) / 1000)
      : RESET_LINK_TIMEOUT;

    if (secondsElapsed < RESET_LINK_TIMEOUT) {
      const errorMessage = `Відправити ще раз можна буде через ${RESET_LINK_TIMEOUT - secondsElapsed} секунд`;
      commit('SET_RESET_LINK_ERROR', errorMessage);
      throw new Error(errorMessage);
    }

    commit('SET_LOADING_RESET_LINK', true);
    commit('SET_RESET_LINK_ERROR', null);

    try {
      await sendResetLinkEmail({ email });
      commit('SET_RESET_EMAIL', email);
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Помилка при формуванні email листа для відновлення пароля';

      commit('SET_RESET_LINK_ERROR', errorMessage);

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Помилка при формуванні email листа для відновлення пароля',
          message: errorMessage,
        }, { root: true });
      }

      const responseCode = error?.response?.status || 0;
      if (responseCode === 422) {
        commit('SET_RESET_LINK_ERROR', error?.response?.data?.data?.email?.join('\n'));
      }

      throw error;
    } finally {
      commit('SET_LOADING_RESET_LINK', false);
    }
  },
  getTimeUntilNextRequest({ state }) {
    if (!state.resetLinkRequestTime) return 0;
    const secondsElapsed = Math.floor((Date.now() - state.resetLinkRequestTime) / 1000);
    const timeLeft = RESET_LINK_TIMEOUT - secondsElapsed;

    return timeLeft > 1 ? timeLeft : 0;
  },
  async changePassword({ commit, dispatch, state }, { token, email, password, passwordConfirmation }) {
    commit('SET_LOADING_CHANGE_PASSWORD', true);
    commit('SET_CHANGE_PASSWORD_ERROR', null);

    try {
      await changePasswordApi({ token, email, password, passwordConfirmation });
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося змінити пароль';

      commit('SET_CHANGE_PASSWORD_ERROR', errorMessage);

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося змінити пароль',
          message: errorMessage,
        }, { root: true });
      }

      const responseCode = error?.response?.status || 0;
      if (responseCode === 422) {
        commit('SET_CHANGE_PASSWORD_ERROR', error?.response?.data?.data?.email?.join('\n'));
      }

      throw error;
    } finally {
      commit('SET_LOADING_CHANGE_PASSWORD', false);
    }
  }
};

const getters = {
  isLoadingResetLink: state => state.isLoadingResetLink,
  isLoadingChangePassword: state => state.isLoadingChangePassword,
  isResetLinkError: state => state.isResetLinkError,
  isChangePasswordError: state => state.changePasswordError,

  isRequestSent: state => !state.isLoadingResetLink && state.email && !state.isResetLinkError,
  getResetEmail: state => state.email,
  getResetLinkErrorMessage: state => state.resetLinkError,
  canSendNewResetLinkRequest: state => {
    if (!state.resetLinkRequestTime) return true;
    const secondsElapsed = Math.floor((Date.now() - state.resetLinkRequestTime) / 1000);

    return secondsElapsed >= RESET_LINK_TIMEOUT;
  },
  getChangePasswordErrorMessage: state => state.changePasswordError,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
